"use client";

import Image from "next/image";
import { useEffect, useState } from "react";

export const BILLIN_MASCOT_SRC = {
  b1: "/billlin_working/mascot-working.png",
  b2: "/billlin_working/mascot-chat.png",
  b3: "/billlin_working/mascot-hero.png",
  b4: "/billlin_working/mascot-checking.png",
  b5: "/billlin_working/mascot-error.png",
  b6: "/billlin_working/mascot-good.png",
  b7: "/billlin_working/mascot-search.png",
  b8: "/billlin_working/mascot-playing.png",
} as const;

export type BillinMascotVariant = keyof typeof BILLIN_MASCOT_SRC;

let billinMascotsPreloaded = false;

/** Warm browser cache for mascot PNGs (call once early in onboarding). */
export function preloadBillinMascots(
  variants: readonly BillinMascotVariant[] = Object.keys(
    BILLIN_MASCOT_SRC,
  ) as BillinMascotVariant[],
) {
  if (typeof window === "undefined") return;
  for (const variant of variants) {
    const img = new window.Image();
    img.src = BILLIN_MASCOT_SRC[variant];
  }
  billinMascotsPreloaded = true;
}

export function ProfileMagicBillinMascot({
  size = "md",
  variant,
  className,
}: {
  size?: "xs" | "sm" | "md";
  variant?: BillinMascotVariant;
  className?: string;
}) {
  const frameClass =
    size === "xs"
      ? "h-14 w-14 shrink-0 overflow-hidden rounded-full bg-aera-offwhite"
      : size === "sm"
        ? "w-full max-w-[200px] md:max-w-[150px]"
        : "w-[60%] md:w-[50%]";
  const imageSizes =
    size === "xs"
      ? "56px"
      : size === "sm"
        ? "(max-width: 768px) 160px, 100px"
        : "(max-width: 768px) 280px, 256px";
  const resolvedVariant = variant ?? (size === "sm" ? "b2" : "b1");
  const src = BILLIN_MASCOT_SRC[resolvedVariant];
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!billinMascotsPreloaded) preloadBillinMascots([resolvedVariant]);
  }, [resolvedVariant]);

  useEffect(() => {
    setLoaded(false);
    const probe = new window.Image();
    probe.src = src;
    if (probe.complete) {
      setLoaded(true);
      return;
    }
    probe.onload = () => setLoaded(true);
    return () => {
      probe.onload = null;
    };
  }, [src]);

  return (
    <div
      className={`relative aspect-square ${size === "xs" ? "" : "mx-auto"} ${frameClass}`}
      aria-hidden
    >
      {!loaded ? (
        <div
          className={`absolute inset-0 animate-pulse bg-aera-lightgray/80 ${
            size === "xs" ? "rounded-full" : "rounded-2xl"
          }`}
        />
      ) : null}
      <Image
        src={src}
        alt=""
        width={500}
        height={500}
        sizes={imageSizes}
        className={[
          className,
          "h-full w-full transition-opacity duration-300",
          size === "xs"
            ? "object-cover object-[center_35%] scale-125"
            : "object-contain",
          loaded ? "opacity-100" : "opacity-0",
        ]
          .filter(Boolean)
          .join(" ")}
        priority={size === "md"}
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
}
